import { useLocation } from "react-router-dom";

import { Eye, Globe } from "lucide-react";

import { StatusStrip, StatusStripButton } from "@/components/StatusStrip";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { userIsFPEmployee } from "@/util";

import {
  disablePreviewMode,
  getTabPreviewState,
  optTabIntoPreviewMode,
  optTabOutOfPreviewMode,
} from "./runtime";
import { pathHasPreviewData } from "./supported-features";

export function PreviewModeBar() {
  const { pathname } = useLocation();
  const accountMetadata = useAccountMetadata();

  const state = getTabPreviewState();
  if (state === "off") return null;
  const preview = state === "preview";
  // Routes without preview handlers show real data even in a preview tab.
  // The real-data bar ignores the account's banner setting: missing that a
  // tab is out of preview mid-demo is the costly mistake.
  if (
    preview &&
    (!pathHasPreviewData(pathname) ||
      accountMetadata?.config?.disableMasqueradeWarningBanner)
  ) {
    return null;
  }

  // The per-tab opt-out is an internal tool.
  const canSwitchTab = userIsFPEmployee();
  const tone = preview ? "preview" : "preview-real-data";

  return (
    <StatusStrip
      tone={tone}
      label="Preview mode"
      icon={
        preview ? (
          <Eye size={16} aria-hidden />
        ) : (
          <Globe size={16} aria-hidden />
        )
      }
    >
      <span className="min-w-0 flex-1 truncate">
        {preview
          ? "PREVIEW: All data in this tab is synthetic."
          : "REAL DATA in this tab. Your other tabs are still in preview."}
      </span>
      {canSwitchTab && (
        <StatusStripButton
          tone={tone}
          onClick={preview ? optTabOutOfPreviewMode : optTabIntoPreviewMode}
        >
          {preview ? "Use real data in this tab" : "Use preview in this tab"}
        </StatusStripButton>
      )}
      <StatusStripButton
        tone={tone}
        onClick={disablePreviewMode}
        title="Exit preview in all tabs"
      >
        Exit
      </StatusStripButton>
    </StatusStrip>
  );
}
